/** @file 2D vector helpers over caller-owned [x, y] tuples; every op is alias-safe. */
import { trig } from './trig';

export type Vec2 = [number, number];

export const vec2 = {
  /** out = [x, y] */
  set(out: Vec2, x: number, y: number): Vec2 {
    out[0] = x;
    out[1] = y;
    return out;
  },
  /** out = a + b */
  add(out: Vec2, a: Vec2, b: Vec2): Vec2 {
    out[0] = a[0] + b[0];
    out[1] = a[1] + b[1];
    return out;
  },
  /** out = a - b */
  sub(out: Vec2, a: Vec2, b: Vec2): Vec2 {
    out[0] = a[0] - b[0];
    out[1] = a[1] - b[1];
    return out;
  },
  /** out = a * s */
  scale(out: Vec2, a: Vec2, s: number): Vec2 {
    out[0] = a[0] * s;
    out[1] = a[1] * s;
    return out;
  },
  /** Length of a. */
  len(a: Vec2): number {
    return trig.hypot(a[0], a[1]);
  },
  /** Dot product. */
  dot(a: Vec2, b: Vec2): number {
    return a[0] * b[0] + a[1] * b[1];
  },
  /** out = a / |a| (the zero vector stays zero). */
  normalize(out: Vec2, a: Vec2): Vec2 {
    const x = a[0];
    const y = a[1];
    const l = trig.hypot(x, y);
    if (l === 0) {
      out[0] = 0;
      out[1] = 0;
      return out;
    }
    out[0] = x / l;
    out[1] = y / l;
    return out;
  },
};
